import { formatCurrency, formatDateLabel, getBankNameByIspb, validatePIXKey } from "@/utils";
import {
  Box,
  Flex,
  Text,
  Divider,
  Button,
  IconButton,
  VStack,
  HStack,
  Select,
  Textarea,
  useColorModeValue,
  Popover, 
  PopoverArrow, 
  PopoverBody, 
  PopoverContent, 
  PopoverTrigger, 
  useDisclosure,
} from "@chakra-ui/react";
import { FiEdit3 } from "react-icons/fi";
import { BsStarFill, BsStar } from "react-icons/bs";
import { useEffect, useState } from "react";
import { Calendar } from "@/components/UI/Calendar";
import { usePayment } from "@/hooks/usePayment";
import { addHours } from "date-fns";
import { TextArea } from "@/components";
import { Tooltip } from "barte-design-system";
interface DatePickerProps {
  date: Date | null;
  onSelect: (d: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
}

interface PixConfirmStepProps {
  onEditAmount?: () => void;
}

export function PixConfirmStep({ onEditAmount }: PixConfirmStepProps) {
    const bgCard = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("#E2E2E2", "gray.700");
    const { pixRecipient, handleEditPixRecipient } = usePayment();

    const {
        amount,
        recipientName,
        recipientDocument,
        bankIspb,
        paymentDate,
        costCenter,
        description,
        isFavorite
    } = pixRecipient;

    const formattedAmount = formatCurrency({ value: amount ?? 0 });

    // estados locais para edição
    const [editedCostCenter, setEditedCostCenter] = useState(costCenter || "");
    const [editedPaymentDate, setEditedPaymentDate] = useState<Date>(
      paymentDate ? new Date(`${paymentDate}T00:00:00`) : new Date()
    );
    const [desc, setDesc] = useState(description || "");

    const dateLabel = formatDateLabel(editedPaymentDate);

    const costCenters = [
        { value: "marketing", label: "Marketing" },
        { value: "vendas", label: "Vendas" },
        { value: "operacional", label: "Operacional" },
        { value: "administrativo", label: "Administrativo" },
    ];

    // salva e atualiza contexto
    const handleSaveEdit = (type: "cost" | "date" | "desc") => {
        if (type === "cost") {
        handleEditPixRecipient({ costCenter: costCenter });
        } else if (type === "date") {
        handleEditPixRecipient({ paymentDate: editedPaymentDate });
        } else if (type === "desc") {
        handleEditPixRecipient({ description: desc });
        }
    };

  function ButtonFavorite() {
    return (
        <Tooltip label={isFavorite ? "Remover dos favoritos" : "Adicionar aos favoritos"} placement="top">
          <Button
              onClick={() => handleEditPixRecipient({ isFavorite: !isFavorite })}
              w="30px"
              h="30px"
              p={0}
              bg="none"
          >
              {isFavorite ? <BsStarFill color="#CB2159" /> : <BsStar color="#CB2159" />}
          </Button>
        </Tooltip>
    )
  }
  useEffect(() => {
    if(!costCenter || !paymentDate) {
      handleEditPixRecipient({ costCenter: editedCostCenter });
      handleEditPixRecipient({ paymentDate: editedPaymentDate })
    }
  }, [])
  return (
    <Flex w="100%" h="100%" justify="center" direction="column" gap={6}>
      {/* header */}
      <Box>
        <Text fontSize="22px" fontWeight="semibold" mb={1}>
          Transferindo
        </Text>
        <Flex align="center" gap={2}>
          <Text fontSize="40px" fontWeight="bold" color="#CB2159">
            {formattedAmount}
          </Text>
          <IconButton
            aria-label="Editar valor"
            icon={<FiEdit3 />}
            variant="ghost"
            size="sm"
            color="#CB2159"
            onClick={onEditAmount}
          />
        </Flex>
      </Box>

      {/* dados do recebedor */}
      <Box border="1px solid" borderColor={borderColor} borderRadius="12px" bg={bgCard}>
        <VStack spacing={0} align="stretch" divider={<Divider />}>
          <Row label="Beneficiário" value={recipientName} iconRight={<ButtonFavorite />} />
          <Row label="CPF" value={recipientDocument} />
          <Row label="Instituição" value={getBankNameByIspb(bankIspb)} />
          <Row label="Agência" value="0001" />
          <Row label="Conta corrente" value="—" />
        </VStack>
      </Box>

      {/* outros campos */}
      <Box border="1px solid" borderColor={borderColor} borderRadius="12px" bg={bgCard}>
        <VStack spacing={0} align="stretch" divider={<Divider />}>
          <EditableRow
            label="Centro de custo"
            value={editedCostCenter}
            action="Editar"
            onSave={() => handleSaveEdit("cost")}
          >
            <Select
              size="sm"
              value={editedCostCenter}
              onChange={(e) => setEditedCostCenter(e.target.value)}
            >
              {costCenters.map((center) => (
                <option key={center.value} value={center.value}>
                  {center.label}
                </option>
              ))}
            </Select>
          </EditableRow>

          <EditableRow
            label="Data da transferência"
            value={dateLabel}
            action="Agendar"
            onSave={() => handleSaveEdit("date")}
          >
            <DatePicker 
              date={editedPaymentDate} 
              onSelect={(d) => {
                if (d) {
                  const local = addHours(d, 3);
                  setEditedPaymentDate(local);
                }
              }}
              minDate={new Date()}
            />
          </EditableRow>

          <EditableRow
            label="Descrição"
            value={desc || ""}
            action="Adicionar"
            onSave={() => handleSaveEdit("desc")}
            placement="bottom"
          >
            <TextArea
              size="sm"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Digite uma descrição"
              maxLength={100}
              currentLength={desc?.length}
              maxLengthBlocks
            />
          </EditableRow>
        </VStack>
      </Box>
    </Flex>
  );
}

/* Componentes auxiliares */
function Row({ label, value, iconRight }: { label: string; value?: string; iconRight?: React.ReactNode }) {
  return (
    <Flex align="center" justify="space-between" p={4}>
      <Box>
        <Text fontSize="sm" color="gray.500">
          {label}
        </Text>
        <Text fontWeight="medium">{value || "—"}</Text>
      </Box>
      {iconRight}
    </Flex>
  );
}

function DatePicker({ date, onSelect, minDate, maxDate }: DatePickerProps) {
  return <Calendar value={date || undefined} onSelect={onSelect} minDate={minDate} maxDate={maxDate} />;
}

/* Editable genérico */
function EditableRow({
  label,
  value,
  action,
  children,
  placement = "left",
  onSave,
}: {
  label: string;
  value?: string;
  action?: string;
  children?: React.ReactNode;
  placement?: "top" | "right" | "left" | "bottom";
  onSave?: () => void;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleSave = () => {
    onSave?.();
    onClose();
  };

  return (
    <Flex align="center" justify="space-between" p={4}>
      <Text fontSize="sm" color="gray.500">
        {label}
      </Text>

      <HStack spacing={2} flexWrap="wrap">
        {value && (
          <Box maxW="220px">
            <Tooltip
              label={value}
              isDisabled={!value || value.length < 30}
            >
              <Text
                maxW="220px"
                textOverflow="ellipsis"
                overflow="hidden"
                whiteSpace="nowrap"
                fontWeight="medium"
                cursor={value.length >= 30 ? "pointer" : "default"}
              >
                {value}
              </Text>
            </Tooltip>
          </Box>
        )}

        {action && (
          <Popover placement={placement} isOpen={isOpen} onClose={onClose} closeOnBlur>
            <PopoverTrigger>
              <Text
                fontSize="sm"
                color="#CB2159"
                fontWeight="medium"
                cursor="pointer"
                _hover={{ textDecoration: "underline" }}
                onClick={onOpen}
              >
                {action}
              </Text>
            </PopoverTrigger>

            <PopoverContent w="auto" minW="240px" borderColor="#E2E2E2" boxShadow="lg">
              <PopoverArrow />
              <PopoverBody>
                <VStack spacing={2} align="stretch">
                  {children}
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="pink"
                    alignSelf="flex-end"
                    onClick={handleSave}
                  >
                    OK
                  </Button>
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}
      </HStack>
    </Flex>
  );
}

